/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 01/11/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the application properties form
 */
class ApplicationPropertiesForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            propertyId: null,
            name: '',
            value: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'name',
                    placeholder: 'Please enter the Application Property Name here',
                    value: '',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Value *',
                    inputName: 'value',
                    placeholder: 'Please enter the Value here',
                    value: '',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const propertyId = this.props.match.params.id;

        // set icons to the header
        if (propertyId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'application-properties',
                pageName: "Application Properties",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/application-properties",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'application-properties',
                pageName: "Application Properties",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/application-properties",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteApplicationProperties);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const propertyId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (propertyId !== undefined) {

            const url = '/application-properties?search=%7BpropertyId:' + propertyId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.propertyId = (data.propertyId !== undefined) ? data.propertyId : null;
                    oldState.name = (data.name !== undefined) ? data.name : null;
                    oldState.value = (data.value !== undefined) ? data.value : '';
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(err => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(err);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteApplicationProperties);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel application properties async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.propertyId !== null) {
            data = {
                propertyId: this.state.propertyId,
                name: this.state.name,
                value: this.state.value
            };
        } else {
            data = {
                name: this.state.name,
                value: this.state.value
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.propertyId !== null) {
                this.updateApplicationProperties(data);
            } else {
                this.createApplicationProperties(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'name':
                if (value === '')
                    error[key] = "Name is Required!";
                else if (value === ' ')
                    error[key] = "Name can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'value':
                if (value === '')
                    error[key] = "Value is Required!";
                else if (value === ' ')
                    error[key] = "Value can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 256))
                    error[key] = "Value can't have more than 256 characters!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create application properties
     * @param data - the data to create a new application properties
     */
    createApplicationProperties = async (data) => {
        await API.post('/application-properties',
            {
                "name": data.name,
                "value": data.value
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Property");
            this.props.history.push('/home/application-properties');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update application properties
     * @param data - updated application property details
     */
    updateApplicationProperties = async (data) => {
        await API.put('/application-properties',
            {
                "propertyId": data.propertyId,
                "name": data.name,
                "value": data.value
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Property");
            this.props.history.push('/home/application-properties');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.propertyId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'application-properties',
                name: this.state.name
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Application Property Details",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a application properties
     * @param data
     */
    deleteApplicationProperties = async (data) => {
        if (data && (this.props.match.path === "/home/application-properties/:id")) {
            await API.delete('/application-properties?propertyId=' + this.state.propertyId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the selected Application Property");
                    this.props.history.push('/home/application-properties');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Application Property is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.name = "";
        newState.value = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/application-properties');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default ApplicationPropertiesForm;
